// forestmarch: the far canopy volume's model shared by the draw (forestvol.js)
// and its CPU REFERENCE (APP_FORESTVOL §4a, §6; APP_GENSURF §2 "every GPU pass
// has a CPU reference"). Pure (no THREE, no DOM): scripts/check-forest.mjs
// runs it on fixtures; pipeline/validate.py _shell_plan ports shellPlan (the
// twin row pins them). The GLSL of forestvol.js ports marchRef / terminalRef
// line for line — change both or neither.
//
// THE SHELL PLAN (which FSH1 tiles draw, at which level). The rings: level c
// (8 / 16 / 32 / 64 m) is drawn from the previous drawn level's radius out to
// min(shell.r<c>_m, vol.range_m) (0 = never; vol.range_m 0 = nothing). Per
// 1024 m tile ONE level: the ring holding its CENTRE distance (past the last
// ring: the last level); a tile draws while its NEAREST point is nearer than
// vol.range_m and its level has a file. A coarser tile beside a finer drawn
// one gets a SKIRT on that side (its lid border edges extruded to the tile's
// y0 − 1 m, facing the finer one): the two lids differ in height at the
// border and the volume would be open there. Draws: one per SHELL_GROUP_M
// group (the tiles of a group merge into one mesh).
//
// THE MARCH (Tevs et al. 2008 maximum mipmaps), in one canopy tile's texel
// space: u, v = (x − X0, z − Z0) / texel, h = (y − y0) × H_Q (the R16UI
// quantum), the max-mip chain H[l] (CANOPY_N >> l)². From the shell entry,
// at level MARCH_L0: inside the cell's max box → descend (level 0: HIT);
// above it → to the box top if the ray reaches it inside the cell (level 0:
// HIT, else descend), else past the cell's exit (+ MARCH_EPS texel) and up a
// level. A step = one iteration; a tile exit is the draw's tile switch (the
// reference: 'exit'). H 0 is no canopy: reaching it ('floor') or below h 0
// (the tile floor, under every drawn ground) is a miss. Out of steps: a HIT at the current point only INSIDE the canopy
// envelope — a falling ray under its level-L box top, a rising ray at the start level (h ≤ H[min(L, MARCH_L0)]: at most
// a 2 × 2-texel box over the crowns); > 0 —, a MISS in an empty box ('steps-empty') or above that envelope
// ('steps-above': a ray that climbed to a coarse level over the crowns — the far sky spikes — or rose over a grazed
// crest out of steps — the hand-off curtain).
// steps 0 (phones): no march, terminalRef at the entry point.
//
// THE TERMINAL (level 0) is a rounded surface inside the texel box: in a
// level-0 cell, surfRef blends the four texel CENTRES around the point (H is a
// point sample of the crown profile, canopygen's splat) — bilinear weights over
// the canopy samples only (empty ones carry no height), coverage C = the
// bilinear of the canopy flags; canopy where C ≥ ½ (a lone texel: a rounded
// crown of radius ½ texel, a forest edge: rounded, serrated), the height the
// weighted mean minus ROUND_DROP × texel × (1 − smoothstep(½, 1, C)): crowns
// round off toward every edge instead of a texel wall; capped at the cell's own
// H (an empty texel paints nothing), so the old box march bounds it unchanged:
// only the old box hit's cell (the ray under its texel's H) is refined — the
// ray segment through the cell (from its entry plane, never before the march's
// start, to its exit) sampled ROUND_NS times and bisected ROUND_BIS times
// (cellHitRef); a miss goes on past the exit at level 1, the old box miss. A
// step between unequal texels is the capped blend's, ≤ half the box's. opt.t
// the texel (m), opt.fp the pixel footprint (m) at the entry (forestvol pk; 0 =
// full precision); opt.terminal 'box' = the red twin.
//
// THE DEPTH CUT (vis-p4-early-out; APP_VISIBILITY §9 P4): the main pass's
// conservative depth (viscull.js / conservativedepth.js: per cell, every ray
// of the cell from every eye is blocked nearer than zCover) bounds the
// opaque depth at EVERY sample of a pixel: the max zCover of the cells under
// the pixel's ray ± one pixel, + CUT_EYE_M. A hit whose depth lies CUT_Q
// past that bound fails the LEQUAL test at every sample, so a march whose
// every later hit lies there discards early — the same pixels. Later hits
// lie horizontally no nearer than the current point − CUT_PAD_M (a refined
// level-0 column reaches back ≤ √2 texels of the coarsest canopy level), and
// the horizontal distance grows along a ray from the eye; the range end
// (a hit past vol.range_m discards) folds into the same bound (cutXZ).
import { H_Q, CANOPY_LEVELS, levelTexel } from './crowns.js';

export const CUT_Q = 8 / 2 ** 24;           // depth quanta (24-bit) between the bound and a cut: 2 quantisations + float32 slack
export const CUT_PAD_M = 1.5 * levelTexel(CANOPY_LEVELS - 1);
export const CUT_EYE_M = 8;                 // zCover → the blocker's distance from the eye: the eye box + the horizon cell (±3 m, 2 m up)
// three's depth (0..1) at view depth vz > 0 (pe = projectionMatrix.elements)
export const depth01 = (pe, vz) => 0.5 * (-pe[10] + pe[14] / vz) + 0.5;
// the view depth whose depth is depth01(zb) + q (Infinity: past the far plane)
export function cutVz(pe, zb, q = CUT_Q) {
  if (!(zb < Infinity)) return Infinity;
  const den = pe[14] / zb + 2 * q;
  return den < 0 ? pe[14] / den : Infinity;
}
// the horizontal distance from the eye past which no hit of the ray shows:
// zb the blocker bound (m, Infinity: none), cosT = the ray · the view axis,
// hxz = |D.xz| of the unit ray, range = vol.range_m
export function cutXZ(pe, zb, cosT, hxz, range, q = CUT_Q) {
  const vz = cutVz(pe, zb, q);
  return vz < Infinity && cosT > 0 ? Math.min(range, (vz / cosT) * hxz) : range;
}
// the march's test at a point dxz (m) from the eye: every later hit is cut
export const cutPast = (dxz, xc, pad = CUT_PAD_M) => dxz - pad > xc;
// the blocker bound of a pixel (forestvol's uCdb lookup, line for line): the
// max zCover of the cells under the ray D's tangent point ± e, e = one pixel
// (pixAng: tangent units per pixel) × (1 + X² + Y²) — every sample ray of the
// pixel lies in those cells; Infinity: off the rectangle or behind the key
// eye. cdb = ConservativeDepth {z, cols, rows, rect, dX, dY, M}; opt.e = the
// red twin's own e
export function cdbBound(cdb, D, pixAng, opt = {}) {
  const M = cdb.M, dz = M[6] * D[0] + M[7] * D[1] + M[8] * D[2];
  if (!(dz < -1e-6)) return Infinity;
  const X = (M[0] * D[0] + M[1] * D[1] + M[2] * D[2]) / -dz, Y = (M[3] * D[0] + M[4] * D[1] + M[5] * D[2]) / -dz;
  const e = opt.e ?? pixAng * (1 + X * X + Y * Y), R = cdb.rect;
  const a0 = Math.floor((X - e - R[0]) / cdb.dX), a1 = Math.floor((Y - e - R[2]) / cdb.dY);
  const b0 = Math.floor((X + e - R[0]) / cdb.dX), b1 = Math.floor((Y + e - R[2]) / cdb.dY);
  if (a0 < 0 || a1 < 0 || b0 >= cdb.cols || b1 >= cdb.rows) return Infinity;
  const z = cdb.z, c = cdb.cols;
  return Math.max(z[a1 * c + a0], z[a1 * c + b0], z[b1 * c + a0], z[b1 * c + b0]);
}

export const SHELL_LEVELS = [8, 16, 32, 64];
export const SHELL_TILE_M = 1024;
export const SHELL_GROUP_M = 4096;         // a draw: 4 × 4 shell tiles
export const SKIRT_DROP_M = 1;             // a skirt's foot: the tile's y0 − 1 m
export const TOP_PAD_M = 1;                // lid + wall tops raised (H quantum, drawn-ground error)
export const MARCH_L0 = 1, MARCH_LMAX = 8, MARCH_EPS = 1e-3, MARCH_MAX = 64;
export const PHONE_COVER = 0.5;            // steps 0: the lid's coverage alpha test
export const SHADE_COV = 0.02, SHADE_UP = 4; // an exhausted hit's colour: the first cell this covered, ≤ this many levels up
// THE ROUNDED TERMINAL (vol-round, orchestrator 2026-10-23: the level-0 box drew
// the canopy as flat-topped columns with texel walls, a lone crown cell as a
// whole hovering cube): ROUND_DROP the rim drop (× the texel m), ROUND_NS samples
// along a cell's ray segment + ROUND_BIS bisections; the shade's light / rim
// constants (forestvol, GLSL only)
export const ROUND_DROP = 2, ROUND_NS = 4, ROUND_BIS = 3;
export const ROUND_KA = 0.45, ROUND_K = [0.55, 1.3], ROUND_RIM = 0.35;
export const NB = [[-1, 0], [1, 0], [0, -1], [0, 1]];   // sides W, E, N, S
// THE SEAT (far-trees 2026-11-03): a wall foot (flag 2) sits at the L0 ground
// − 0.5 m (the bake), the DRAWN ground under it is the ground tree's node there,
// off L0 by up to that level's tol (a coarse node lower than L0 showed a strip
// of forest floor under every forest edge: the volume "not on the floor"). The
// VS sinks each foot to SEAT_PAD_M under the drawn ground, at most SEAT_MAX_M
// (forestvol.js seatCells lowers every P3 cell box by it); never raised. Fed
// per group by forestvol _seat when the ground tree's gen moves. foot, g: the
// foot's y and the drawn ground there (NaN: nothing drawn → null, keep).
// THE COVER (same note): an exhausted ray (out of steps: a coarse level-L
// box face) is kept with probability = the canopy coverage A.a under the
// hand-off's dither noise (hoDither) — sparse far stands speckle like the
// punched hand-off band instead of painting solid boxes (forestvol uDith bit 1;
// GPU-only, no CPU twin: a discard of a pixel that is not a surface hit)
export const SEAT_PAD_M = 0.5, SEAT_MAX_M = 128;   // 128: a level-9 node's tol (measured: feet up to 91 m over an L6/L7 read)
export const seatSink = (foot, g) => (Number.isFinite(g) ? Math.min(SEAT_MAX_M, Math.max(0, foot - (g - SEAT_PAD_M))) : null);

// the rings of a live (or ceiling) class block: [[c, lo, hi]]
export function shellRings(shell, range) {
  const out = [];
  if (!(range > 0)) return out;
  let lo = 0;
  for (const c of SHELL_LEVELS) {
    const r = Math.min(Number(shell && shell[`r${c}_m`]) || 0, range);
    if (r > lo) { out.push([c, lo, r]); lo = r; }
  }
  return out;
}

// meta.forest.shell -> {T, lv: Map c -> Map 'i_j' -> nt, tiles: [[i, j]]}
export function shellIndex(shell) {
  if (!shell || shell.layout !== 'FSH1') throw new Error(`forestvol: meta.forest.shell layout ${shell && shell.layout}`);
  const lv = new Map(), all = new Map();
  for (const l of shell.levels) {
    const m = new Map();
    for (const [i, j, , nt] of l.list) { m.set(`${i}_${j}`, nt); all.set(`${i}_${j}`, [i, j]); }
    lv.set(l.cell_m, m);
  }
  const tiles = [...all.values()].sort((a, b) => a[1] - b[1] || a[0] - b[0]);
  return { T: shell.tile_m, lv, tiles, dirs: new Map(shell.levels.map((l) => [l.cell_m, l.dir])) };
}

// the plan at (x, z): {lvl: Map 'i_j' -> c, tiles [{i, j, c, nt}], skirts
// [{i, j, c, side}], tris (shell + skirts; skirt tris need edges(c, i, j)
// -> [W, E, N, S] border lid edges, else 0), draws (groups)}
export function shellPlan(ix, x, z, rings, range, edges = null) {
  const T = ix.T, lvl = new Map(), tiles = [];
  if (rings.length) {
    for (const [i, j] of ix.tiles) {
      const dc = Math.hypot((i + 0.5) * T - x, (j + 0.5) * T - z);
      const dx = Math.max(i * T - x, 0, x - (i + 1) * T), dz = Math.max(j * T - z, 0, z - (j + 1) * T);
      if (Math.hypot(dx, dz) >= range) continue;
      const r = rings.find(([, a, b]) => a <= dc && dc < b);
      const c = r ? r[0] : rings[rings.length - 1][0];
      const nt = ix.lv.get(c).get(`${i}_${j}`) || 0;
      if (!nt) continue;
      lvl.set(`${i}_${j}`, c);
      tiles.push({ i, j, c, nt });
    }
  }
  let tris = 0;
  const skirts = [], groups = new Set();
  for (const t of tiles) {
    tris += t.nt;
    groups.add(`${Math.floor((t.i * T) / SHELL_GROUP_M)}_${Math.floor((t.j * T) / SHELL_GROUP_M)}`);
    NB.forEach(([a, b], side) => {
      const n = lvl.get(`${t.i + a}_${t.j + b}`);
      if (n === undefined || n >= t.c) return;
      skirts.push({ i: t.i, j: t.j, c: t.c, side });
      if (edges) tris += 2 * edges(t.c, t.i, t.j)[side];
    });
  }
  return { lvl, tiles, skirts, tris, draws: groups.size };
}

// a decoded FSH1 tile (forestshell.js decodeShell) -> its lid border edges
// per side [W, E, N, S] as vertex pairs [[a, b]…] (lid triangles only)
export function borderEdges(d) {
  const ox = d.i * d.tileM, oz = d.j * d.tileM, M = d.tileM;
  const on = (k, side) => {
    const lx = d.x[k] - ox, lz = d.z[k] - oz;
    return side === 0 ? Math.abs(lx) < 1e-3 : side === 1 ? Math.abs(lx - M) < 1e-3 : side === 2 ? Math.abs(lz) < 1e-3 : Math.abs(lz - M) < 1e-3;
  };
  const out = [[], [], [], []];
  for (let t = 0; t < d.ntTop; t++) {
    for (let e = 0; e < 3; e++) {
      const a = d.index[3 * t + e], b = d.index[3 * t + ((e + 1) % 3)];
      for (let s = 0; s < 4; s++) if (on(a, s) && on(b, s)) out[s].push([a, b]);
    }
  }
  return out;
}

// the steps a pass marches with (the optic: its own tunable)
export function stepsFor(live, optic) {
  const v = live && live.vol;
  if (!v) return 0;
  return Math.max(0, Math.min(MARCH_MAX, (optic ? v.steps_optic : v.steps) | 0));
}

// THE CLIMB (pbc-forest 2026-10-24: the live shell's entry lies up to ~30 m
// over the crowns; the old march started at MARCH_L0 and climbed one level on
// every box exit, then spent a step to come back down wherever the coarser box
// held taller crowns — 57 % of the far fragments out of 16 steps at the
// owner's pose, the flat slabs past the cut line): a box exit climbs one level
// only where the point lies over the next coarser box (the fetched height is
// the next step's: GLSL reuses it, the same fetches as before, fewer steps);
// the start (and a tile switch) climbs from MARCH_L0 while it does (fetches,
// never a step). Max-mips: the coarser box's air is the finer boxes' air. The
// exhaust guard keeps MARCH_L0 for a rising ray. opt.climb 'tevs' in marchRef
// = the red twin (start at MARCH_L0, +1 on every exit). f = the box fetches
// (levels ≥ 1; the climb's counted, a reused one not).

// THE MARCH in one tile (see the header): H = [Uint16Array (N >> l)²], q0 =
// [u, h, v], dq = the direction in the same units, steps (≤ MARCH_MAX in the
// draw). Out of steps: a HIT only where the point lies inside the canopy
// envelope (falling: h ≤ H[L]; rising: h ≤ H[min(L, MARCH_L0)]; > 0), a MISS
// where the level-L box is empty ('steps-empty') or the point is above it
// ('steps-above') — orchestrator 2026-10-23: always-a-hit painted the shell's
// own silhouette, a hit in any box holding canopy the coarse boxes' air over
// the crowns, a rising ray's hit under the level-L box top the air over a
// grazed crest.
// Strictly paints less, never invents canopy. opt.exhaust 'miss' | 'hit' |
// 'box' | 'coarse' = the red twins (always a miss / always a hit / a hit in
// any box holding canopy / a rising ray's hit under the level-L box top);
// opt.climb 'tevs' the old climb (THE CLIMB). -> {hit, why ('hit' | 'steps' | 'steps-empty' |
// 'steps-above' | 'exit' | 'floor' | 'below' | 'up' | 'zero' | 'cut': opt.cut(u,
// h, v) true at a step's start, THE DEPTH CUT), k (steps
// used), f (box fetches), u, h, v, L}
export function marchRef(H, q0, dq, steps, opt = {}) {
  const N = Math.round(Math.sqrt(H[0].length));
  let u = q0[0], h = q0[1], v = q0[2], L = MARCH_L0, k = 0, tt = 0;   // tt: the ray parameter travelled
  let f = 0, hc = -1;   // the box fetches; the climb's box height at (u, v, L) (the next step's, reused)
  const du = dq[0], dh = dq[1], dv = dq[2];
  const nudge = MARCH_EPS / Math.max(Math.abs(du), Math.abs(dv), 1e-9);
  const drop = roundDrop(opt.t);
  const out = (hit, why) => ({ hit, why, k, f, u, h, v, L });
  const climb = (cap) => {
    hc = -1;
    for (let n = 0; n < cap && L < MARCH_LMAX; n++) { const b = boxH(H, u, v, L + 1); f++; if (!(h > b)) break; L++; hc = b; }
  };
  if (!(steps > 0)) return out(false, 'zero');
  if (opt.climb !== 'tevs' && u >= 0 && v >= 0 && u < N && v < N) climb(MARCH_LMAX);
  for (; k < steps; k++) {
    if (opt.cut && opt.cut(u, h, v)) return out(false, 'cut');   // THE DEPTH CUT (forestvol's loop top)
    if (u < 0 || v < 0 || u >= N || v >= N) return out(false, 'exit');
    if (h < 0) return out(false, 'below');
    const cs = 2 ** L, s = N >> L, ci = Math.floor(u / cs), cj = Math.floor(v / cs);
    if (L === 0 && opt.terminal !== 'box') {
      // the rounded terminal: the old box's candidate (the ray under the texel's own H), refined on the surface capped at it
      const W = windowH(H[0], N, ci, cj), hm = W[4];
      const [tin, te] = cellSpan(ci, cj, u, v, du, dv, tt);
      if (te === Infinity) return out(false, 'up');
      if (hm && Math.min(h + dh * tin, h + dh * te) <= hm) {
        const a = cellHitRef(W, ci, cj, [u + du * tin, h + dh * tin, v + dv * tin], dq, te - tin, drop, opt.fp ? opt.fp / (opt.t || 2) : 0);
        if (a >= 0) { const b = tin + a; u += du * b; h += dh * b; v += dv * b; k++; return out(true, 'hit'); }
      }
      if (!hm && dh < 0 && -h / dh < te) { k++; return out(false, 'floor'); }   // an empty texel: the ray reaches the tile floor
      const a = te + nudge;
      u += du * a; h += dh * a; v += dv * a; tt += a;
      L = 1;   // the old box miss: on at level 1
      continue;
    }
    const hm = hc >= 0 ? hc : (f++, H[L][cj * s + ci]);
    hc = -1;
    if (h <= hm) {
      if (L === 0) return hm ? out(true, 'hit') : out(false, 'floor');
      L--;
      continue;
    }
    const lx = ci * cs, lz = cj * cs;
    const tx = du > 0 ? (lx + cs - u) / du : du < 0 ? (lx - u) / du : Infinity;
    const tz = dv > 0 ? (lz + cs - v) / dv : dv < 0 ? (lz - v) / dv : Infinity;
    const te = Math.min(tx, tz);
    if (dh < 0) {
      const tp = (hm - h) / dh;
      if (tp < te) {
        if (!hm) { k++; return out(false, 'floor'); }   // an empty box: the ray reaches the tile floor
        u += du * tp; v += dv * tp; h = hm; tt += tp;
        if (L === 0) { k++; return out(true, 'hit'); }
        L--;
        continue;
      }
    }
    if (te === Infinity) return out(false, 'up');
    const a = te + nudge;
    u += du * a; h += dh * a; v += dv * a; tt += a;
    if (opt.climb === 'tevs') L = Math.min(L + 1, MARCH_LMAX);
    else if (u >= 0 && v >= 0 && u < N && v < N) climb(1);   // up only over the coarser box (THE CLIMB)
  }
  if (opt.exhaust === 'miss') return out(false, 'steps');
  if (opt.exhaust === 'hit') return out(true, 'steps');
  if (u < 0 || v < 0 || u >= N || v >= N) return out(false, 'exit');
  if (L === 0 && opt.terminal !== 'box') {
    // out of steps at level 0: the envelope is the surface itself
    const ci = Math.floor(u), cj = Math.floor(v), W = windowH(H[0], N, ci, cj);
    if (!W[4]) return out(false, 'steps-empty');
    const S = surfRef(W, ci, cj, u, v, drop);
    return S.c >= 0.5 && h <= S.s ? out(true, 'steps') : out(false, 'steps-above');
  }
  const hb = boxH(H, u, v, L);
  if (!hb) return out(false, 'steps-empty');
  if (opt.exhaust === 'box') return out(true, 'steps');
  // a falling ray: the level-L box top; a rising one (a grazed crest): the
  // envelope at the start level (≤ L: max-mips are monotone, ≤ hb)
  const he = opt.exhaust === 'coarse' || dh < 0 ? hb : boxH(H, u, v, Math.min(L, MARCH_L0));
  return h <= he ? out(true, 'steps') : out(false, 'steps-above');
}

// the max-mip box height at (u, v) on level L (texel units of level 0)
export function boxH(H, u, v, L) {
  const N = Math.round(Math.sqrt(H[0].length)), s = N >> L, cs = 2 ** L;
  return H[L][Math.min(s - 1, Math.max(0, Math.floor(v / cs))) * s + Math.min(s - 1, Math.max(0, Math.floor(u / cs)))];
}

// the rim drop in H units at texel m t (the gate's tiles: their level's texel)
export const roundDrop = (t = 2) => ROUND_DROP * t * H_Q;

// the 3×3 level-0 window around cell (ci, cj), indices clamped to the tile
// (row-major from the NW): what surfRef reads -> [9]
export function windowH(H0, N, ci, cj) {
  const W = new Array(9);
  for (let b = -1; b <= 1; b++) for (let a = -1; a <= 1; a++) {
    W[(b + 1) * 3 + a + 1] = H0[Math.min(N - 1, Math.max(0, cj + b)) * N + Math.min(N - 1, Math.max(0, ci + a))];
  }
  return W;
}

// the ray's span through cell (ci, cj) from the current point (u, v): [tin
// (the entry plane, ≤ 0, never before the march's start: −tt), te (the exit)]
export function cellSpan(ci, cj, u, v, du, dv, tt) {
  const tx = du > 0 ? (ci + 1 - u) / du : du < 0 ? (ci - u) / du : Infinity;
  const tz = dv > 0 ? (cj + 1 - v) / dv : dv < 0 ? (cj - v) / dv : Infinity;
  const bx = du > 0 ? (ci - u) / du : du < 0 ? (ci + 1 - u) / du : -Infinity;
  const bz = dv > 0 ? (cj - v) / dv : dv < 0 ? (cj + 1 - v) / dv : -Infinity;
  return [Math.min(0, Math.max(bx, bz, -tt)), Math.min(tx, tz)];
}

// THE TERMINAL SURFACE at (u, v) in cell (ci, cj) from its window W (see the
// header) -> {s (H units, capped at the texel's own H), c (coverage 0..1), sh
// (the blend before the rim drop), su (uncapped: the shading's smooth surface)}
export function surfRef(W, ci, cj, u, v, drop) {
  if (!W[4]) return { s: -1, c: 0, sh: 0, su: -1 };   // an empty texel paints nothing
  const x = u - 0.5, z = v - 0.5, i0 = Math.floor(x), j0 = Math.floor(z), fx = x - i0, fz = z - j0;
  let c = 0, sh = 0;
  for (let b = 0; b < 2; b++) for (let a = 0; a < 2; a++) {
    const hk = W[(j0 + b - cj + 1) * 3 + i0 + a - ci + 1];
    if (!hk) continue;
    const w = (a ? fx : 1 - fx) * (b ? fz : 1 - fz);
    c += w; sh += w * hk;
  }
  sh /= c;
  const e = Math.min(1, Math.max(0, (c - 0.5) / 0.5)), g = e * e * (3 - 2 * e), su = sh - drop * (1 - g);
  return { s: Math.min(su, W[4]), c, sh, su };
}

// the first point of the ray segment q + dq·a, a in [0, len] (the cell's
// span), inside the canopy (C ≥ ½, h ≤ s): ROUND_NS samples, ROUND_BIS
// bisections -> a | −1
// fp: the pixel footprint (texels) — the samples ≥ one footprint apart, one
// bisection past a footprint of a texel (0: full precision, the gate's rays)
export function cellHitRef(W, ci, cj, q, dq, len, drop, fp = 0) {
  const inside = (a) => {
    const S = surfRef(W, ci, cj, q[0] + dq[0] * a, q[2] + dq[2] * a, drop);
    return S.c >= 0.5 && q[1] + dq[1] * a <= S.s;
  };
  if (inside(0)) return 0;
  const ns = fp > 0 ? Math.min(ROUND_NS, Math.max(1, Math.ceil(len * Math.hypot(dq[0], dq[2]) / fp))) : ROUND_NS, nb = fp >= 1 ? 1 : ROUND_BIS;
  let lo = 0;
  for (let n = 1; n <= ns; n++) {
    let hi = (len * n) / ns;
    if (!inside(hi)) { lo = hi; continue; }
    for (let b = 0; b < nb; b++) { const m = 0.5 * (lo + hi); if (inside(m)) hi = m; else lo = m; }
    return hi;
  }
  return -1;
}

// steps 0 (phones): the entry point itself — a lid point shows the canopy
// colour where its coverage (A alpha at level la) passes PHONE_COVER, a wall
// point where it lies under the canopy height (H at level lw: the serrated
// wall top). A lid point seen by a RISING ray (dy ≥ 0: from below, the lid
// edge-on over a crest) shows only within TOP_PAD_M of the canopy at level lw
// under it — the lid sits at the coarse phoneLidH max and would paint the
// crowns' pattern into the sky (the hand-off curtain, orchestrator
// 2026-10-23); from above (dy < 0) the lid is the canopy top. A = [Float32Array
// 4·(N >> l)²] (linear 0..1), H as marchRef. opt.rising false = the red twin.
// -> {hit, l (the level shaded)}
export function terminalRef(A, H, u, h, v, wall, la = 0, lw = 1, dy = -1, opt = {}) {
  const N = Math.round(Math.sqrt(H[0].length));
  const at = (l) => { const s = N >> l, c = 2 ** l; return Math.min(s - 1, Math.max(0, Math.floor(v / c))) * s + Math.min(s - 1, Math.max(0, Math.floor(u / c))); };
  if (wall) return { hit: h <= H[lw][at(lw)], l: lw };
  if (dy >= 0 && opt.rising !== false && h > H[lw][at(lw)] + TOP_PAD_M * H_Q) return { hit: false, l: la };
  return { hit: A[la][4 * at(la) + 3] >= PHONE_COVER, l: la };
}

// the shade level of a hit (forestvol's pick, line for line): lp the pixel
// footprint level (log2 of the pixel's metres over the texel, ≥ 0). A
// converged hit (L 0) and every hit whose footprint is coarser take
// round(max(L, lp)); an EXHAUSTED hit starts at the footprint, not its march
// level (L's 2^L-texel mip painted the far canopy as lumps, orchestrator
// 2026-10-23, finding C), and climbs while its own cell covers < SHADE_COV
// (an empty texel's rgb 0 would paint black), never past L, at most SHADE_UP
// levels. cov[l] = Float32Array (N >> l)² of the coverage mips (the mean
// coverage, canopygen FS_ABMIP); opt.pick 'march' | 'footprint' = the red
// twins (the old L floor / no climb). -> level
export function shadeLevelRef(cov, u, v, lp, L, ex, opt = {}) {
  const N = Math.round(Math.sqrt(cov[0].length));
  const lo = ex && opt.pick !== 'march' ? 0 : L;
  let lf = Math.min(MARCH_LMAX, Math.max(lo, Math.floor(Math.max(lo, lp) + 0.5)));
  if (!ex || opt.pick === 'footprint' || opt.pick === 'march') return lf;
  const i = Math.min(N - 1, Math.max(0, Math.floor(u))), j = Math.min(N - 1, Math.max(0, Math.floor(v)));
  for (let s = 0; s < SHADE_UP; s++) {
    if (lf >= L || cov[lf][(j >> lf) * (N >> lf) + (i >> lf)] >= SHADE_COV) break;
    lf++;
  }
  return lf;
}

// the class's depth mode: every marching class writes the HIT's depth
// ('frag', gl_FragDepth) — Quest's shell depth + 8 m let a hit under the
// terrain (a canopy column's side seen through a gap) cover the ground and
// the town (owner go 2026-10-23, Q2); phones (steps 0) draw the sunk shell,
// its own depth ('shell'). -> 'frag' | 'shell'
export const depthMode = (cls) => (cls === 'phone-lo' || cls === 'phone-hi' ? 'shell' : 'frag');

// the phone lid: a lid / wall-top vertex sinks to the canopy (the max of the
// 2 × 2 cells of level l around it, + TOP_PAD), never above where it was
// (steps 0 only; the march keeps the proven bake shell). -> h (H units)
export function phoneLidH(H, u, v, h, l) {
  const N = Math.round(Math.sqrt(H[0].length)), s = N >> l, c = 2 ** l;
  const i0 = Math.floor(u / c - 0.5), j0 = Math.floor(v / c - 0.5);
  let m = 0;
  for (let b = 0; b < 2; b++) for (let a = 0; a < 2; a++) {
    const i = Math.min(s - 1, Math.max(0, i0 + a)), j = Math.min(s - 1, Math.max(0, j0 + b));
    m = Math.max(m, H[l][j * s + i]);
  }
  return Math.min(h, m + TOP_PAD_M * H_Q);
}

// the canopy level whose cell spans a shell cell's vertex footprint (2 cells)
export function phoneLevel(cellM, texelM) {
  return Math.max(0, Math.min(MARCH_LMAX, Math.ceil(Math.log2((2 * cellM) / texelM))));
}
